import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Empty, Input, Modal, Popconfirm, Radio, Space, Tag, Tooltip, Typography, message } from 'antd'
import {
  CloudUploadOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FolderOpenOutlined,
  LoadingOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  RightOutlined,
  ScanOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import {
  batchRerunWithTemplate,
  browseFolder,
  createAutoProcessTemplate,
  currentProcessingConfigOf,
  deleteChain,
  deleteGroup,
  getAssetRootsStatus,
  getJob,
  getUploadConfig,
  listChains,
  pauseAutoProcess,
  planImport,
  renameChain,
  renameGroup,
  rerunGroupWithTemplate,
  rerunWithTemplate,
  resumeAutoProcess,
  retryChainStage,
  revealChain,
  revealGroup,
  runImport,
  type AssetChainOut,
  type AssetGroupOut,
  type AssetRootsStatus,
  type ChainListSummary,
  type UploadConfig,
} from '../api/client'
import { notifyTemplateListChanged } from '../utils/templateEvents'
import AutoProcessTemplateBar from '../components/AutoProcessTemplateBar'
import RerunTemplateModal from '../components/RerunTemplateModal'
import TemplateSaveDialog from '../components/TemplateSaveDialog'
import UploadModal from '../components/UploadModal'
import { FourStageFlowHeader } from '../components/StageFlow'
import ChainEditorModal from '../components/editor/ChainEditorModal'
import BackgroundEditorModal from '../components/editor/BackgroundEditorModal'
import ShadowEditorModal from '../components/editor/ShadowEditorModal'
import ChainRow from '../components/ChainRow'
import StatusFilterTabs, { type StatusFilter } from '../components/StatusFilterTabs'
import SelectionActionBar from '../components/SelectionActionBar'
import RenameModal from '../components/RenameModal'
import { useChainDeleteActions } from '../components/useChainDeleteActions'

/** Target of the shared "选择自动处理模板重新处理" dialog — one dialog, three
 * possible callers (spec 十五): a single chain, a whole group, or a
 * multi-selected batch. */
type RerunTarget =
  | { kind: 'chain'; id: string; label: string }
  | { kind: 'group'; id: string; label: string }
  | { kind: 'batch'; ids: string[]; label: string }

const POLL_MS = 2000

export default function Home() {
  const [chains, setChains] = useState<AssetChainOut[]>([])
  const [groups, setGroups] = useState<AssetGroupOut[]>([])
  const [summary, setSummary] = useState<ChainListSummary | null>(null)
  const [rootsStatus, setRootsStatus] = useState<AssetRootsStatus | null>(null)

  const [q, setQ] = useState('')
  const [groupId, setGroupId] = useState<string | undefined>(undefined)
  const [status, setStatus] = useState<StatusFilter>('')
  const [selected, setSelected] = useState<string[]>([])
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

  const [uploadOpen, setUploadOpen] = useState(false)
  const [uploadConfig, setUploadConfig] = useState<UploadConfig | null>(null)

  const [importOpen, setImportOpen] = useState(false)
  const [pickedPath, setPickedPath] = useState('')
  const [pickingFolder, setPickingFolder] = useState(false)
  const [importPlan, setImportPlan] = useState<Awaited<ReturnType<typeof planImport>>['data'] | null>(null)
  const [planning, setPlanning] = useState(false)
  const [conflictPolicy, setConflictPolicy] = useState<'skip' | 'overwrite' | 'cancel'>('skip')
  const [importRunning, setImportRunning] = useState(false)
  const [importResultMsg, setImportResultMsg] = useState('')

  const [renameTarget, setRenameTarget] = useState<{ type: 'chain' | 'group'; id: string; current: string } | null>(
    null,
  )
  const [renameValue, setRenameValue] = useState('')

  const [editorChain, setEditorChain] = useState<AssetChainOut | null>(null)
  const [backgroundEditorChain, setBackgroundEditorChain] = useState<AssetChainOut | null>(null)
  const [shadowEditorChain, setShadowEditorChain] = useState<AssetChainOut | null>(null)

  const [rerunTarget, setRerunTarget] = useState<RerunTarget | null>(null)
  const [saveAsTemplateChain, setSaveAsTemplateChain] = useState<AssetChainOut | null>(null)

  // ASSET-UX-CONSISTENCY-A1: `loadChains` is called from several
  // independent triggers that can be in flight at once — the POLL_MS
  // interval below, an Editor's `onSaved`, delete/rerun handlers, filter
  // changes — with no guarantee the HTTP responses land in request order.
  // Without a guard, a slow poll response issued *before* an Editor save
  // can resolve *after* the save's own post-save `loadChains()` and stomp
  // the fresh background/shadow_generation back down to the stale value
  // (Problem 3, Case E) purely by request racing, independent of anything
  // being wrong server-side. `requestSeq` makes only the most-recently
  // *issued* request allowed to commit state.
  const requestSeqRef = useRef(0)
  const loadChains = useCallback(async () => {
    const seq = ++requestSeqRef.current
    const { data } = await listChains({
      q: q || undefined,
      group_id: groupId,
      status: status || undefined,
      limit: 3000,
    })
    if (seq !== requestSeqRef.current) return
    setChains(data.chains)
    setGroups(data.groups)
    setSummary(data.summary)
  }, [q, groupId, status])

  // ASSET-UX-CONSISTENCY-A1: applied synchronously, in the same tick as the
  // save response, so the row shows the new background/shadow_generation
  // (and thus the busted URL) the instant the modal closes — never waiting
  // on the next `loadChains()` round-trip to "happen to" land after it.
  const mergeSavedChain = useCallback((updated: AssetChainOut) => {
    setChains((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
  }, [])

  const loadRootsStatus = useCallback(async () => {
    try {
      const { data } = await getAssetRootsStatus()
      setRootsStatus(data)
    } catch {
      // non-fatal
    }
  }, [])

  useEffect(() => {
    loadChains().catch(() => undefined)
    loadRootsStatus().catch(() => undefined)
    getUploadConfig()
      .then(({ data }) => setUploadConfig(data))
      .catch(() => undefined)
  }, [loadChains, loadRootsStatus])

  useEffect(() => {
    const t = window.setInterval(() => {
      loadChains().catch(() => undefined)
    }, POLL_MS)
    return () => window.clearInterval(t)
  }, [loadChains])

  const pickFolder = async () => {
    setPickingFolder(true)
    try {
      const { data } = await browseFolder()
      if (data.cancelled || !data.path) return
      setPickedPath(data.path)
      setPlanning(true)
      try {
        const { data: plan } = await planImport(data.path)
        setImportPlan(plan)
      } finally {
        setPlanning(false)
      }
    } catch (e) {
      message.error((e as Error).message)
    } finally {
      setPickingFolder(false)
    }
  }

  const openImportModal = () => {
    setPickedPath('')
    setImportPlan(null)
    setConflictPolicy('skip')
    setImportResultMsg('')
    setImportOpen(true)
  }

  const startImport = async () => {
    if (!pickedPath) return
    setImportRunning(true)
    try {
      const { data } = await runImport(pickedPath, conflictPolicy)
      await pollImportJob(data.job_id)
    } catch (e) {
      message.error((e as Error).message)
      setImportRunning(false)
    }
  }

  const pollImportJob = (jobId: string) =>
    new Promise<void>((resolve) => {
      const timer = window.setInterval(async () => {
        try {
          const { data: job } = await getJob(jobId)
          await loadChains()
          if (['succeeded', 'failed', 'cancelled'].includes(job.status)) {
            window.clearInterval(timer)
            setImportRunning(false)
            if (job.status === 'succeeded') {
              setImportResultMsg(job.message || '导入完成')
              message.success(job.message || '导入完成')
              setImportOpen(false)
            } else {
              message.error(job.message || '导入失败')
            }
            resolve()
          }
        } catch {
          // keep polling
        }
      }, 500)
    })

  const toggleSelect = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

  const toggleCollapse = (gid: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(gid)) next.delete(gid)
      else next.add(gid)
      return next
    })

  const groupsByPath = useMemo(() => {
    const m = new Map<string, AssetGroupOut>()
    for (const g of groups) m.set(g.relative_folder_path, g)
    return m
  }, [groups])

  const blocks = useMemo(() => {
    const result: Array<{ group: AssetGroupOut | null; rows: AssetChainOut[] }> = []
    let current: { group: AssetGroupOut | null; rows: AssetChainOut[] } | null = null
    for (const c of chains) {
      const g = c.group_relative_path ? groupsByPath.get(c.group_relative_path) || null : null
      const key = g?.id || null
      if (!current || current.group?.id !== key) {
        current = { group: g, rows: [] }
        result.push(current)
      }
      current.rows.push(c)
    }
    return result
  }, [chains, groupsByPath])

  const onRetryStage = async (id: string, stage: 'transparent' | 'background' | 'shadow') => {
    try {
      await retryChainStage(id, stage)
      message.info('已排队重新生成')
      await loadChains()
    } catch (e) {
      message.error((e as Error).message)
    }
  }

  const { onDeleteOriginal, onDeleteTransparent, onDeleteBackground, onDeleteShadow, onDeleteChain, deleteDialog } =
    useChainDeleteActions(loadChains, setChains, (id) => setSelected((prev) => prev.filter((x) => x !== id)))

  const onDeleteGroupBlock = (group: AssetGroupOut) => {
    Modal.confirm({
      // See centering note in `useChainDeleteActions.tsx` — static
      // Modal.confirm doesn't inherit ConfigProvider's `modal.centered`.
      centered: true,
      title: '永久删除整组',
      icon: <ExclamationCircleOutlined />,
      content: `将永久删除分组「${group.display_name}」下全部 ${group.image_count} 张图片的所有资产及相关记录。删除后无法恢复，请确认。`,
      okText: '永久删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        await deleteGroup(group.id)
        message.success('已删除整组')
        await loadChains()
      },
    })
  }

  const openRename = (type: 'chain' | 'group', id: string, current: string) => {
    setRenameTarget({ type, id, current })
    setRenameValue(current)
  }

  const confirmRename = async () => {
    if (!renameTarget || !renameValue.trim()) return
    try {
      if (renameTarget.type === 'chain') await renameChain(renameTarget.id, renameValue.trim())
      else await renameGroup(renameTarget.id, renameValue.trim())
      message.success('改名成功')
      setRenameTarget(null)
      await loadChains()
    } catch (e) {
      message.error((e as Error).message)
    }
  }

  const batchAction = async (kind: 'retry-transparent' | 'reprocess' | 'delete') => {
    if (!selected.length) return
    if (kind === 'delete') {
      const ids = [...selected]
      Modal.confirm({
        // See centering note in `useChainDeleteActions.tsx`.
        centered: true,
        title: `永久删除 ${ids.length} 条资产链`,
        icon: <ExclamationCircleOutlined />,
        content: '将永久删除所选图片的原图、透明底图、色底图、阴影图及相关记录。删除后无法恢复，请确认。',
        okText: '永久删除',
        okButtonProps: { danger: true },
        cancelText: '取消',
        // UI-first, same as the single-chain delete (`useChainDeleteActions`):
        // synchronous onOk so the modal closes and the selection/rows clear
        // immediately, while the actual deletes run in the background.
        onOk: () => {
          setChains((prev) => prev.filter((c) => !ids.includes(c.id)))
          setSelected([])
          ;(async () => {
            const failed: string[] = []
            for (const id of ids) {
              try {
                await deleteChain(id)
              } catch {
                failed.push(id)
              }
            }
            if (failed.length) {
              message.error(`${failed.length}/${ids.length} 条删除失败，已恢复列表，请重试`)
              await loadChains()
            } else {
              message.success('批量删除完成')
            }
          })()
        },
      })
      return
    }
    if (kind === 'reprocess') {
      setRerunTarget({ kind: 'batch', ids: [...selected], label: `已选 ${selected.length} 张` })
      return
    }
    for (const id of selected) await retryChainStage(id, 'transparent').catch(() => undefined)
    message.success(`已对 ${selected.length} 张图片排队处理`)
    setSelected([])
    await loadChains()
  }

  // Shared handler behind the single/group/batch "重跑全部流程" dialog (spec
  // 十五) — dispatches to whichever of the three thin API wrappers matches
  // `rerunTarget.kind`, all of which ultimately share the same backend
  // template-resolution + rerun implementation.
  const confirmRerun = async (templateId: string) => {
    if (!rerunTarget) return
    try {
      if (rerunTarget.kind === 'chain') {
        await rerunWithTemplate(rerunTarget.id, templateId)
      } else if (rerunTarget.kind === 'group') {
        await rerunGroupWithTemplate(rerunTarget.id, templateId)
      } else {
        await batchRerunWithTemplate(rerunTarget.ids, templateId)
        setSelected([])
      }
      message.success('已排队重新处理')
      setRerunTarget(null)
      await loadChains()
    } catch (e) {
      message.error((e as Error).message || '重跑失败')
    }
  }

  // "设为模板" (spec 五/六/七): capture this chain's *actual current*
  // effective config (including any manual editor tweaks) — never the
  // template it originally came from — and save it as a brand-new template
  // via the exact same creation call the top template bar's "新建模板" uses.
  const handleSaveAsTemplate = async (name: string, description: string) => {
    if (!saveAsTemplateChain) return
    try {
      await createAutoProcessTemplate({
        name,
        description: description || undefined,
        params: currentProcessingConfigOf(saveAsTemplateChain),
        set_active: false,
      })
      notifyTemplateListChanged()
      message.success(`已将「${saveAsTemplateChain.original_filename}」的当前参数保存为新模板「${name}」`)
      setSaveAsTemplateChain(null)
    } catch (e) {
      message.error((e as Error).message || '保存模板失败')
    }
  }

  const toggleAutoProcess = async () => {
    if (!summary) return
    if (summary.auto_process_paused) await resumeAutoProcess()
    else await pauseAutoProcess()
    await loadChains()
  }

  return (
    <div className="pipeline-page">
      {rootsStatus && !rootsStatus.ok && (
        <Alert
          type="warning"
          showIcon
          closable
          style={{ margin: '12px 16px 0' }}
          message="固定资产目录存在问题（仅本地存储模式诊断信息，不影响云端 R2 资产读写）"
          description={
            <>
              <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                {rootsStatus.errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
              <ul style={{ margin: '4px 0', paddingLeft: 20, color: 'rgba(0,0,0,0.65)' }}>
                {rootsStatus.roots.map((r) => (
                  <li key={r.kind}>
                    {r.kind}: {r.path} {r.error ? `（${r.error}）` : r.exists ? '✓ 正常' : '不存在'}
                  </li>
                ))}
              </ul>
              <Button size="small" onClick={loadRootsStatus}>
                重新检查
              </Button>
            </>
          }
        />
      )}
      <div className="pipeline-toolbar">
        <div className="toolbar-section toolbar-section-left">
          <div className="page-heading">
            <div className="page-title">基础图片资产</div>
            <div className="page-subtitle">原图 → 透明底图 → 色底图 → 阴影图</div>
          </div>
        </div>

        <div className="toolbar-section toolbar-section-center">
          {uploadConfig?.browser_upload_enabled && (
            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              onClick={() => setUploadOpen(true)}
              data-testid="open-upload-btn"
            >
              浏览器上传
            </Button>
          )}
          <Button
            type={uploadConfig?.browser_upload_enabled ? 'default' : 'primary'}
            icon={<FolderOpenOutlined />}
            onClick={openImportModal}
          >
            导入文件夹
          </Button>
          <Tooltip title="刷新">
            <Button icon={<ScanOutlined />} onClick={loadChains} />
          </Tooltip>
          <Input
            placeholder="搜索文件名"
            prefix={<SearchOutlined />}
            style={{ width: 200 }}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onPressEnter={() => loadChains()}
            allowClear
          />
        </div>

        <div className="toolbar-section toolbar-section-right">
          {summary && (
            <span className="summary-text">
              共 {summary.total} · 已完成 {summary.done} · 处理中 {summary.processing} · 失败 {summary.failed}
            </span>
          )}
          <Button
            size="small"
            icon={summary?.auto_process_paused ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
            onClick={toggleAutoProcess}
          >
            {summary?.auto_process_paused ? '继续自动处理' : '暂停自动处理'}
          </Button>
        </div>
      </div>

      <AutoProcessTemplateBar />

      <StatusFilterTabs value={status} onChange={setStatus} />

      <div className="pipeline-body">
        <div className="pipeline-sidebar">
          <div className={`sidebar-item ${!groupId ? 'sidebar-item-active' : ''}`} onClick={() => setGroupId(undefined)}>
            全部图片
          </div>
          {groups.map((g) => (
            <div
              key={g.id}
              className={`sidebar-item ${groupId === g.id ? 'sidebar-item-active' : ''}`}
              onClick={() => setGroupId(g.id)}
            >
              <FolderOpenOutlined /> {g.display_name}
              <span className="sidebar-count">{g.image_count}</span>
            </div>
          ))}
        </div>

        <div className="pipeline-main">
          <SelectionActionBar
            count={selected.length}
            onRetryTransparent={() => batchAction('retry-transparent')}
            onReprocess={() => batchAction('reprocess')}
            onDelete={() => batchAction('delete')}
            onClear={() => setSelected([])}
          />

          <div className="pipeline-header-row">
            <div className="pipeline-header-select" />
            <FourStageFlowHeader />
            <div className="pipeline-header-actions">操作</div>
          </div>

          {chains.length === 0 ? (
            <Empty description="还没有图片，点击左上角“导入文件夹”开始" style={{ marginTop: 80 }} />
          ) : (
            blocks.map((block, bi) => (
              <div key={block.group?.id || `loose-${bi}`} className={block.group ? 'chain-group-block' : undefined}>
                {block.group && (
                  <GroupHeader
                    group={block.group}
                    collapsed={collapsed.has(block.group.id)}
                    onToggle={() => toggleCollapse(block.group!.id)}
                    onRename={() => openRename('group', block.group!.id, block.group!.display_name)}
                    onReprocess={() =>
                      setRerunTarget({ kind: 'group', id: block.group!.id, label: `「${block.group!.display_name}」` })
                    }
                    onDelete={() => onDeleteGroupBlock(block.group!)}
                    onReveal={() => revealGroup(block.group!.id).catch(() => undefined)}
                  />
                )}
                {(!block.group || !collapsed.has(block.group.id)) &&
                  block.rows.map((c) => (
                    <ChainRow
                      key={c.id}
                      chain={c}
                      selected={selected.includes(c.id)}
                      onToggle={() => toggleSelect(c.id)}
                      onEdit={() => setEditorChain(c)}
                      onEditBackground={() => setBackgroundEditorChain(c)}
                      onEditShadow={() => setShadowEditorChain(c)}
                      onRetryStage={(stage) => onRetryStage(c.id, stage)}
                      onDeleteOriginal={() => onDeleteOriginal(c)}
                      onDeleteTransparent={() => onDeleteTransparent(c)}
                      onDeleteBackground={() => onDeleteBackground(c)}
                      onDeleteShadow={() => onDeleteShadow(c)}
                      onDeleteChain={() => onDeleteChain(c)}
                      onRename={() => openRename('chain', c.id, c.original_filename.replace(/\.[^.]+$/, ''))}
                      onReprocess={() => setRerunTarget({ kind: 'chain', id: c.id, label: `「${c.original_filename}」` })}
                      onSaveAsTemplate={() => setSaveAsTemplateChain(c)}
                      onReveal={() => revealChain(c.id).catch(() => undefined)}
                    />
                  ))}
              </div>
            ))
          )}
        </div>
      </div>

      <UploadModal
        open={uploadOpen}
        config={uploadConfig}
        onClose={() => setUploadOpen(false)}
        onAnyCompleted={() => loadChains()}
      />

      <Modal
        title="导入文件夹"
        open={importOpen}
        onCancel={() => (!importRunning ? setImportOpen(false) : undefined)}
        footer={null}
        width={560}
      >
        <Typography.Paragraph type="secondary">
          选择一个外部照片文件夹。其中根层单张图片和一级子文件夹会被完整复制进固定的原图目录（
          <code>original-pics</code>），原文件夹内容不会被修改。
        </Typography.Paragraph>
        <button type="button" className="folder-picker-zone" onClick={pickFolder} disabled={pickingFolder || importRunning}>
          {pickingFolder ? (
            <Space direction="vertical" size={8}>
              <LoadingOutlined spin style={{ fontSize: 28 }} />
              <span>正在打开文件夹选择器…</span>
            </Space>
          ) : pickedPath ? (
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <FolderOpenOutlined style={{ fontSize: 28, color: 'var(--accent)' }} />
              <Typography.Text strong ellipsis style={{ maxWidth: '100%' }}>
                {pickedPath}
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                点击重新选择
              </Typography.Text>
            </Space>
          ) : (
            <Space direction="vertical" size={8}>
              <FolderOpenOutlined style={{ fontSize: 28, color: 'var(--accent)' }} />
              <span>点击选择文件夹</span>
            </Space>
          )}
        </button>

        {planning && (
          <div style={{ marginTop: 12 }}>
            <LoadingOutlined spin /> 正在扫描目录……
          </div>
        )}

        {importPlan && !planning && (
          <div className="import-plan-panel">
            <div>根层单图：{importPlan.loose_images} 张</div>
            <div>
              一级子文件夹：
              {Object.entries(importPlan.groups).length
                ? Object.entries(importPlan.groups).map(([name, count]) => (
                    <Tag key={name}>
                      {name}（{count}）
                    </Tag>
                  ))
                : ' 无'}
            </div>
            <div>忽略的非图片文件：{importPlan.ignored_non_image}</div>
            <div>合计将导入：{importPlan.total_images} 张</div>
            {importPlan.conflicts.length > 0 && (
              <>
                <div style={{ color: '#fa8c16', marginTop: 8 }}>
                  发现 {importPlan.conflicts.length} 个同名冲突，请选择处理方式：
                </div>
                <Radio.Group value={conflictPolicy} onChange={(e) => setConflictPolicy(e.target.value)}>
                  <Space direction="vertical">
                    <Radio value="skip">仅导入新增文件（跳过已存在，推荐）</Radio>
                    <Radio value="overwrite">覆盖同名原图并重新生成衍生图</Radio>
                    <Radio value="cancel">取消本次导入</Radio>
                  </Space>
                </Radio.Group>
              </>
            )}
          </div>
        )}

        {importResultMsg && <div style={{ marginTop: 12, color: '#52c41a' }}>{importResultMsg}</div>}

        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Space>
            <Button onClick={() => setImportOpen(false)} disabled={importRunning}>
              取消
            </Button>
            <Button type="primary" loading={importRunning} disabled={!pickedPath || planning} onClick={startImport}>
              开始导入
            </Button>
          </Space>
        </div>
      </Modal>

      {deleteDialog}

      <RenameModal
        open={!!renameTarget}
        title={renameTarget?.type === 'group' ? '重命名分组' : '重命名图片'}
        value={renameValue}
        onChange={setRenameValue}
        onOk={confirmRename}
        onCancel={() => setRenameTarget(null)}
      />

      {editorChain && (
        <ChainEditorModal
          chainId={editorChain.id}
          filename={editorChain.original_filename}
          open={!!editorChain}
          initialChain={editorChain}
          onClose={() => setEditorChain(null)}
          onSaved={(updated) => {
            mergeSavedChain(updated)
            loadChains()
          }}
        />
      )}

      {backgroundEditorChain && backgroundEditorChain.transparent_url && (
        <BackgroundEditorModal
          chainId={backgroundEditorChain.id}
          filename={backgroundEditorChain.original_filename}
          transparentUrl={backgroundEditorChain.transparent_url}
          initialParams={backgroundEditorChain.background_params}
          open={!!backgroundEditorChain}
          onClose={() => setBackgroundEditorChain(null)}
          onSaved={(updated) => {
            mergeSavedChain(updated)
            loadChains()
          }}
        />
      )}

      {shadowEditorChain && shadowEditorChain.transparent_url && (
        <ShadowEditorModal
          chainId={shadowEditorChain.id}
          filename={shadowEditorChain.original_filename}
          transparentUrl={shadowEditorChain.transparent_url}
          backgroundParams={shadowEditorChain.background_params}
          initialShadowParams={shadowEditorChain.shadow_params}
          open={!!shadowEditorChain}
          onClose={() => setShadowEditorChain(null)}
          onSaved={(updated) => {
            mergeSavedChain(updated)
            loadChains()
          }}
        />
      )}

      <RerunTemplateModal
        open={!!rerunTarget}
        title={rerunTarget ? `选择自动处理模板重新处理${rerunTarget.label}` : ''}
        originalOptionLabel={rerunTarget?.kind === 'batch' || rerunTarget?.kind === 'group' ? '各自原模板' : '原模板（恢复原模板参数）'}
        onCancel={() => setRerunTarget(null)}
        onConfirm={confirmRerun}
      />

      <TemplateSaveDialog
        open={!!saveAsTemplateChain}
        initialName={saveAsTemplateChain ? saveAsTemplateChain.original_filename.replace(/\.[^.]+$/, '') + ' 模板' : ''}
        onCancel={() => setSaveAsTemplateChain(null)}
        onSubmit={handleSaveAsTemplate}
      />
    </div>
  )
}

function GroupHeader({
  group,
  collapsed,
  onToggle,
  onRename,
  onReprocess,
  onDelete,
  onReveal,
}: {
  group: AssetGroupOut
  collapsed: boolean
  onToggle: () => void
  onRename: () => void
  onReprocess: () => void
  onDelete: () => void
  onReveal: () => void
}) {
  return (
    <div className="group-header">
      <span className="group-collapse-btn" onClick={onToggle}>
        <RightOutlined rotate={collapsed ? 0 : 90} />
      </span>
      <FolderOpenOutlined />
      <strong>{group.display_name}</strong>
      <span className="group-stats">
        {group.image_count} 张图片 · 已完成 {group.done_count} · 处理中 {group.processing_count} · 失败{' '}
        {group.failed_count}
      </span>
      <Space size={4} className="group-actions">
        <Button size="small" onClick={onReprocess}>
          全组重新生成
        </Button>
        <Tooltip title="组改名">
          <Button size="small" icon={<EditOutlined />} onClick={onRename} />
        </Tooltip>
        <Tooltip title="打开文件夹">
          <Button size="small" icon={<FolderOpenOutlined />} onClick={onReveal} />
        </Tooltip>
        <Tooltip title="删除整组">
          <Popconfirm title="确定删除整组？" onConfirm={onDelete}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Tooltip>
      </Space>
    </div>
  )
}
